'use client';

import Link from 'next/link';
import Navbar from '../../../components/Navbar';
import Footer from '../../../components/Footer';
import { Phone, Mail, MapPin, Clock, ChevronRight, MessageSquare, Building2, Users } from 'lucide-react';

export default function ContactPage() {
    const specializedTeams = [
        {
            title: 'Property Buyers Support',
            desc: 'Need help finding the right plot, villa, or apartment? Our dedicated search assistants are here.',
            email: 'buy@dreamacres.com',
            phone: '+91 98765 43211',
            icon: <Users className="w-5 h-5 text-primary" />
        },
        {
            title: 'Agent & Seller Partnerships',
            desc: 'Advertise your properties, manage listings, or request premium marketing plans.',
            email: 'partners@dreamacres.com',
            phone: '+91 98765 43212',
            icon: <Building2 className="w-5 h-5 text-primary" />
        },
        {
            title: 'Technical Support',
            desc: 'Facing payment failures, listing edit bugs, or account creation issues?',
            email: 'tech@dreamacres.com',
            phone: '+91 98765 43213',
            icon: <MessageSquare className="w-5 h-5 text-primary" />
        }
    ];

    return (
        <>
            <Navbar />
            <main className="min-h-screen bg-slate-50/50">
                {/* Hero / Breadcrumb Section */}
                <section className="relative overflow-hidden text-white py-20 lg:py-24">
                    <img
                        src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1600&auto=format&fit=crop&q=80"
                        alt="Contact DreamAcres"
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-900/80 to-primary-dark/85" />
                    <div className="absolute inset-0 bg-black/25" />

                    <div className="container-custom relative z-10">
                        <nav className="flex items-center gap-2 text-xs lg:text-sm text-white/80 mb-6 font-medium">
                            <Link href="/" className="hover:text-white transition-colors">Home</Link>
                            <ChevronRight className="w-4 h-4 opacity-70" />
                            <span className="text-white font-semibold">Contact Us</span>
                        </nav>
                        <div className="max-w-2xl">
                            <h1 className="text-4xl lg:text-5xl font-extrabold tracking-tight mb-4 leading-tight">
                                Let's Start a <br className="hidden md:inline" />
                                <span className="text-white/90">Conversation</span>
                            </h1>
                            <p className="text-base lg:text-lg text-white/80 leading-relaxed font-light">
                                Have questions about a listing, need dashboard assistance, or want to partner with us? Reach out directly. Our property support specialists are ready to assist.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Main Content Section */}
                <section className="py-12 lg:py-16 relative z-20">
                    <div className="container-custom">

                        {/* Two Column Layout (Specialized Teams & Map Details) */}
                        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-start">
                            {/* Specialized Teams (Left 3 columns) */}
                            <div className="lg:col-span-3 space-y-8">
                                <div>
                                    <h2 className="text-2xl lg:text-3xl font-extrabold text-slate-800 mb-3">
                                        Connect with Specialized Desks
                                    </h2>
                                    <p className="text-slate-500 max-w-xl">
                                        For specific queries regarding buyer search support, technical system issues, or seller partnerships, speak to our dedicated teams.
                                    </p>
                                </div>

                                <div className="space-y-6">
                                    {specializedTeams.map((team, idx) => (
                                        <div
                                            key={idx}
                                            className="bg-white rounded-xl p-6 border border-slate-100 hover:border-primary/10 hover:shadow-sm transition-all duration-200 flex items-start gap-5"
                                        >
                                            <div className="w-10 h-10 bg-primary/5 rounded-lg flex items-center justify-center shrink-0">
                                                {team.icon}
                                            </div>
                                            <div className="space-y-3 flex-1">
                                                <div>
                                                    <h4 className="font-bold text-slate-800">{team.title}</h4>
                                                    <p className="text-sm text-slate-500 mt-1">{team.desc}</p>
                                                </div>
                                                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm pt-1">
                                                    <a href={`mailto:${team.email}`} className="text-primary font-semibold hover:underline flex items-center gap-1.5">
                                                        <Mail className="w-4 h-4" />
                                                        {team.email}
                                                    </a>
                                                    <a href={`tel:${team.phone.replace(/\s+/g, '')}`} className="text-slate-600 hover:text-slate-900 flex items-center gap-1.5">
                                                        <Phone className="w-4 h-4 text-slate-400" />
                                                        {team.phone}
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Address & Visual Map Location (Right 2 columns) */}
                            <div className="lg:col-span-2 space-y-6">
                                <div className="bg-white rounded-2xl border border-slate-100 p-8 shadow-[0_4px_25px_rgba(0,0,0,0.02)] space-y-6">
                                    <div>
                                        <h3 className="text-xl font-bold text-slate-800 mb-2">Corporate Office</h3>
                                        <p className="text-sm text-slate-500 flex items-center gap-2">
                                            <Clock className="w-4 h-4 text-primary" />
                                            Monday - Sunday : 10:00 AM to 08:00 PM
                                        </p>
                                    </div>

                                    {/* Office Info card details */}
                                    <div className="bg-slate-50 rounded-xl p-5 border border-slate-100/50 space-y-4">
                                        <div className="flex gap-3">
                                            <MapPin className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                                            <div>
                                                <h5 className="font-semibold text-sm text-slate-700">Physical Address</h5>
                                                <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                                                    No 7, 53, Williams Rd, Melapudur,<br />
                                                    Cantonment, Tiruchirappalli,<br />
                                                    Tamil Nadu 620001
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex gap-3 pt-2 border-t border-slate-200/60">
                                            <Mail className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                                            <div>
                                                <h5 className="font-semibold text-sm text-slate-700">Email Address</h5>
                                                <a href="mailto:dreamacress@gmail.com" className="text-sm text-primary hover:underline mt-0.5 block font-medium">
                                                    dreamacress@gmail.com
                                                </a>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Premium Map Container with Iframe */}
                                    <div className="relative rounded-xl overflow-hidden shadow-inner border border-slate-100 bg-slate-100 group">
                                        <iframe
                                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3919.138100094973!2d78.6815143!3d10.8007333!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3baaf5573d92269b%3A0xd5c183fcdc1c80a4!2sDream%20Acres!5e0!3m2!1sen!2sin!4v1787638692792!5m2!1sen!2sin"
                                            width="100%"
                                            height="220"
                                            style={{ border: 0, filter: 'grayscale(0.1) contrast(1.1) brightness(0.95)' }}
                                            allowFullScreen=""
                                            loading="lazy"
                                            referrerPolicy="strict-origin-when-cross-origin"
                                            title="Dream Acres Tiruchirappalli Office"
                                            className="transition-all duration-300 group-hover:scale-105"
                                        ></iframe>
                                        <div className="absolute bottom-3 left-3 right-3 bg-white/95 backdrop-blur-sm p-3 rounded-lg border border-white/20 shadow-sm flex items-center justify-between">
                                            <div>
                                                <span className="text-[11px] font-bold text-primary uppercase tracking-wider">Dream Acres HQ</span>
                                                <p className="text-[10px] text-slate-500">Cantonment, Trichy</p>
                                            </div>
                                            <a
                                                href="https://maps.google.com/?q=Dream+Acres,+No+7,+53,+Williams+Rd,+Melapudur,+Cantonment,+Tiruchirappalli,+Tamil+Nadu+620001"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="bg-primary hover:bg-primary-dark text-white text-xs font-semibold px-3 py-1.5 rounded-md transition-colors"
                                            >
                                                Get Directions
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
